import {Tooltip} from 'platform/components';
import {Text} from 'platform/foundation';

import {isNotNil, isNumber} from 'ramda-adjunct';

import {EMPTY_CELL_PLACEHOLDER} from '../../constants/cellPlaceholder';
import {CellRendererProps} from '../../types/CellRendererProps';
import {CellStyle} from '../../types/CellStyle';
import {TooltipGetter} from '../../types/TooltipGetter';
import {formatTimeToResponse} from '../../utils/formatTimeToResponse';
import {getCellClassName} from '../../utils/getCellClassName';
import {getStylesToRender} from '../../utils/getStylesToRender';
import {getTooltipProps} from '../../utils/getTooltipProps';

type TimeIntervalCellRendererProps = CellRendererProps<number | null> & {
  getTooltipProps?: TooltipGetter;
};

export function TimeIntervalCellRenderer(props: TimeIntervalCellRendererProps) {
  const className = getCellClassName(props.isWrap);
  const cellStyles = getStylesToRender<CellStyle>(props);
  const tooltipProps = getTooltipProps(props.row, props.getTooltipProps);

  // Format only finite numbers (isNumber alone accepts NaN/Infinity and would
  // render "NaN days"; ramda-adjunct's isValidNumber doesn't narrow the type,
  // so pair the narrowing isNumber with Number.isFinite); anything else
  // non-nil passes through verbatim.
  const cellValue =
    isNumber(props.value) && Number.isFinite(props.value) // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? formatTimeToResponse(props.value)
      : isNotNil(props.value)
        ? String(props.value)
        : (props.emptyCellValue ?? EMPTY_CELL_PLACEHOLDER);

  return (
    <Tooltip {...tooltipProps}>
      <div className={className}>
        <Text color={cellStyles?.textColor} noWrap={!props.isWrap} size="small">
          {cellValue}
        </Text>
      </div>
    </Tooltip>
  );
}
